import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import type { ArticleData } from '@/mocks/articles';

interface ArticlePageProps {
  article: ArticleData;
}

export default function ArticlePage({ article }: ArticlePageProps) {
  const siteUrl = import.meta.env.VITE_SITE_URL || 'https://itaobuy.help';
  const articleUrl = `${siteUrl}/${article.slug}`;
  const articleJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: article.title,
    description: article.subtitle,
    image: article.heroImg,
    datePublished: '2026-06-15',
    dateModified: article.date,
    author: {
      '@type': 'Organization',
      name: 'itaobuy.help',
      url: siteUrl,
    },
    publisher: {
      '@type': 'Organization',
      name: 'itaobuy.help',
      url: siteUrl,
      logo: {
        '@type': 'ImageObject',
        url: 'https://static.readdy.ai/image/2df560051ef0cafd8dabf8a049058ba4/62f99c0415358bd5388d5ed1df9ac1ff.jpeg',
      },
    },
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': articleUrl,
    },
  };

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'instant' });
  }, []);

  return (
    <main className="min-h-screen bg-background-50">
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(articleJsonLd) }}
      />
      <nav className="sticky top-0 z-50 bg-background-50/95 backdrop-blur-md border-b border-background-200/60">
        <div className="flex items-center justify-between h-16 px-6 md:px-10 max-w-6xl mx-auto">
          <Link to="/" className="font-heading text-xl md:text-2xl font-semibold text-foreground-950 tracking-tight whitespace-nowrap">
            itaobuy<span className="text-primary-500">.help</span>
          </Link>
          <div className="flex items-center gap-4">
            <Link to="/" className="text-sm text-foreground-600 hover:text-foreground-950 transition-colors whitespace-nowrap">
              <i className="ri-arrow-left-line mr-1"></i> Back to Home
            </Link>
            <a
              href="https://kaspreadsheet.com"
              target="_blank"
              rel="noopener nofollow"
              className="inline-flex items-center px-4 py-2 text-sm font-semibold text-background-50 bg-primary-500 rounded-full hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer"
            >
              Buy It Now
            </a>
          </div>
        </div>
      </nav>

      <article className="max-w-4xl mx-auto px-6 md:px-10 py-12 md:py-20">
        <header className="mb-12">
          <div className="flex items-center gap-2 text-xs font-medium text-primary-500 uppercase tracking-widest mb-4">
            <Link to="/" className="hover:text-primary-600 transition-colors">Home</Link>
            <span className="text-foreground-300">/</span>
            <span>Guides</span>
          </div>
          <h1 className="text-3xl md:text-5xl lg:text-6xl font-heading font-bold text-foreground-950 leading-tight">
            {article.title}
          </h1>
          <p className="mt-4 text-base md:text-lg text-foreground-600 leading-relaxed max-w-3xl">
            {article.subtitle}
          </p>
          <div className="mt-6 flex items-center gap-4 text-sm text-foreground-500">
            <span>Updated: {article.date}</span>
            <span className="w-1 h-1 rounded-full bg-foreground-300"></span>
            <span>{article.readTime}</span>
          </div>
        </header>

        <div className="relative rounded-2xl overflow-hidden mb-14 h-[300px] md:h-[420px]">
          <img
            src={article.heroImg}
            alt={article.title}
            title={article.title}
            className="w-full h-full object-cover object-top"
          />
        </div>

        <div className="space-y-8">
          {article.sections.map((section, idx) => (
            <section key={idx}>
              <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-950 mt-12 mb-4">
                <a id={section.anchor} className="anchor"></a>
                {section.heading}
              </h2>
              {section.paragraphs.map((p, pIdx) => (
                <p key={pIdx} className="text-base text-foreground-700 leading-relaxed mt-4 first:mt-0">
                  {p}
                </p>
              ))}
              {section.hasTable && section.tableHeaders && section.tableRows && (
                <div className="overflow-x-auto mt-6">
                  <table className="w-full text-left border-collapse">
                    <thead>
                      <tr className="border-b-2 border-background-300">
                        {section.tableHeaders.map((h, hIdx) => (
                          <th key={hIdx} className={`py-3 text-sm font-semibold whitespace-nowrap ${hIdx === 0 ? 'pr-4 text-foreground-700' : hIdx === 1 ? 'px-4 text-primary-600' : 'pl-4 text-foreground-500'}`}>
                            {h}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {section.tableRows.map((row, rIdx) => (
                        <tr key={rIdx} className="border-b border-background-200/60 last:border-0">
                          {row.map((cell, cIdx) => (
                            <td key={cIdx} className={`py-3.5 text-sm whitespace-nowrap ${cIdx === 0 ? 'pr-4 text-foreground-800 font-medium' : cIdx === 1 ? 'px-4 text-foreground-700' : 'pl-4 text-foreground-500'}`}>
                              {cell}
                            </td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
              {section.hasCTA && (
                <div className="mt-8 p-6 md:p-8 rounded-2xl bg-primary-100/50 border border-primary-200/50">
                  <p className="text-lg font-heading font-semibold text-foreground-900 mb-3">Start Your First Spreadsheet Order</p>
                  <p className="text-sm text-foreground-700 leading-relaxed mb-5">
                    Join thousands of satisfied buyers who have already switched to the transparent, efficient <strong>itaobuyspreadsheet</strong> method for sourcing from China&apos;s top marketplaces.
                  </p>
                  <div className="flex flex-col sm:flex-row gap-3">
                    <a
                      href="https://kaspreadsheet.com"
                      target="_blank"
                      rel="noopener nofollow"
                      className="inline-flex items-center justify-center px-6 py-3 text-sm font-semibold text-background-50 bg-primary-500 rounded-full hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer"
                    >
                      Buy It Now
                      <i className="ri-arrow-right-line ml-2"></i>
                    </a>
                    <Link
                      to="/"
                      className="inline-flex items-center justify-center px-6 py-3 text-sm font-medium text-primary-600 border border-primary-300 rounded-full hover:bg-primary-50 transition-colors whitespace-nowrap"
                    >
                      Back to Homepage
                    </Link>
                  </div>
                </div>
              )}
            </section>
          ))}
        </div>

        <footer className="mt-16 pt-8 border-t border-background-200/60">
          <nav className="flex flex-wrap items-center gap-4 text-sm">
            <Link to="/" className="text-foreground-600 hover:text-foreground-950 transition-colors whitespace-nowrap">
              <i className="ri-home-4-line mr-1"></i> Home
            </Link>
            <span className="text-foreground-300">·</span>
            <a href="https://kaspreadsheet.com" target="_blank" rel="noopener nofollow" className="text-foreground-600 hover:text-foreground-950 transition-colors whitespace-nowrap">
              Main Platform <i className="ri-external-link-line ml-0.5"></i>
            </a>
            <span className="text-foreground-300">·</span>
            <Link to="/" className="text-foreground-600 hover:text-foreground-950 transition-colors whitespace-nowrap">
              All Guides
            </Link>
          </nav>
        </footer>
      </article>
    </main>
  );
}